import {put, call} from 'redux-saga/effects';
import Question from "../../entities/question";
import * as Actions from "../actions/questionActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type QuestionAction = {
    questionId: string;
    question: Question;
    questions: Question[];
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneQuestion(action: QuestionAction) {
	const session = tabSession.captureGeneration();
	try {
		const question: Question = yield call(api.findOne, 'question', action.questionId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.QUESTION_FETCH_ONE_SUCCESS, questionId: action.questionId, question});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.QUESTION_FETCH_ONE_FAILURE, questionId: action.questionId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {QuestionAction} action
 */
export function* saveQuestion(action: QuestionAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    // console.debug('⮂ QUESTION_SAVE saga', action.question?.id);
    
	if (action.question === undefined || action.question === null) {
		console.error('saveQuestion received a null');
		throw new Error('saveQuestion received a null');
	}

	if (!(action.question instanceof Question)) {
		action.question = new Question(action.question);
	}

	try {
		// Save to IndexedDB
		const addResult = yield call(idb.add, 'question', action.question);
		if (addResult.isErr()) {
			console.error(`⮂ saveQuestion: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			session.assertCurrent();
			yield put({type: Actions.QUESTION_SAVE_FAILURE, question: action.question, status: 500, message: addResult.unwrapErr().message});
			return;
		}
		reportQueued(true);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.QUESTION_SAVE, question: action.question});

		// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
		// removes it when it saves; a draft typed somewhere else may be newer than this copy.
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue);
	}
	catch(err) {
		if (session.signal.aborted) { reportQueued(false); return; }
		reportQueued(false);
		yield put({type: Actions.QUESTION_SAVE_FAILURE, question: action.question, status: 500, err});

		console.error('Error saving to database', err);
	}
}

/**
 * TODO: Update for saveMany
 * @param {QuestionAction} action
 * @param {Question[]} action.questions
 */
export function* saveManyQuestions(action: QuestionAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    // console.debug('⮂ QUESTION_SAVE_MANY saga', action.questions.map(question => question.id).join(','));
    
    if (!Array.isArray(action.questions)) {
        console.error('saveManyQuestions expected an array, received', action.questions);
        throw new Error('saveManyQuestions expected an array');
    }

	try {
		const toSave: Question[] = [];

		// Save each item to IndexedDB
		for(let i = 0; i < action.questions.length; i++) {
			const question = action.questions[i];
			question.persisted = false;
			toSave.push(question);
		}

		const addResult = yield call(idb.add, 'question', toSave);
		if (addResult.isErr()) {
			console.error(`⮂ saveManyQuestions: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			session.assertCurrent();
			yield put({type: Actions.QUESTION_SAVE_MANY_FAILURE, questions: action.questions, status: 500, message: addResult.unwrapErr().message});
			return;
		}
		reportQueued(true);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.QUESTION_SAVE_MANY, questions: toSave});
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) { reportQueued(false); return; }
		reportQueued(false);
		yield put({type: Actions.QUESTION_SAVE_MANY_FAILURE, questions: action.questions, status: 500, err});

		console.error(`Error saving ${action.questions.length} questions to database`, err);
	}
}

/**
 * @param {QuestionAction} action
 */
export function* removeQuestion(action: QuestionAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
	if (!userId || action.question === undefined || action.question === null || !(action.question instanceof Question)) {
			console.error('removeQuestion expected type Question, received', action.question);
			throw new Error('removeQuestion expected type Question');
		}

	try {
		// Mark as deleted
		action.question.status = 'D';
		action.question.addedOn = newHRTimestamp();
		action.question.addedBy = userId;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'question', action.question);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeQuestion: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.QUESTION_REMOVE_FAILURE, question: action.question, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.QUESTION_REMOVE, question: action.question});
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.QUESTION_REMOVE_FAILURE, question: action.question, status: 500, err});

		console.error('Error deleting from database', err);
	}
}

/**
	* Restore the last version of this Question.
	*
	* @param {QuestionAction} action
	*/
export function* restoreQuestion(action: QuestionAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreQuestion expected getCurrentUserId() to return a value');
        return;
    }
    
	if (action.question === undefined || action.question === null) {
        console.error('restoreQuestion expected a Question, received null');
        throw new Error('restoreQuestion expected a Question, received null');
    }
    else if (!(action.question instanceof Question)) action.question = new Question(action.question); 
    
	try {
		// Mark as restored
		action.question.status = 'U';
		action.question.addedOn = newHRTimestamp();
		action.question.addedBy = userId;
		action.question.restore = true;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'question', action.question);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreQuestion: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.QUESTION_RESTORE_FAILURE, question: action.question, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.QUESTION_RESTORE, question: action.question});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.QUESTION_RESTORE_FAILURE, question: action.question, status: 500, err});

		console.error('Error restoring from database', err);
	}
}